"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatOnboarding/PlanWelcomeStep/OnboardingPlanWelcomeModalContent";

import { Icon } from "@ospweb/sdk-ui";
import { useEffect, useState } from "react";
import { cn } from "utils";
import { useIsPc } from "@/src/hooks";
import type { OnboardingPlanWelcomeModalContentProps } from "./types";
import { gaTrackingAttrs } from "@/src/utils/tracking";
import { buildOpenReplayLabel } from "@/src/utils/openreplay";
import { AppImage } from "@/src/components/NextImage";

const I18n = I18nUtil.createScoped(Translations);

/** Figma `45185:412591` 背景图（与 LoadingStep 同目录约定） */
const BACKGROUND_SRC = "/assets/images/onboard-bg.webp";
/** Figma `45223:73036` 渐变遮罩，叠在 webp 之上 */
const BACKGROUND_OVERLAY =
  "linear-gradient(-89.78deg, rgba(44, 45, 10, 0.086) 14.132%, rgba(44, 45, 10, 0.49) 65.188%)";

/**
 * 标题入场动画开始前的等待时间（ms）。
 *
 * 为何需要延迟：
 * - H5：`DrawerH5`（Ant Design Drawer）自下而上滑入，约 300ms
 * - PC：`OspModal` 淡入/缩放，时长同量级
 * - 若挂载即播 CSS 动画，弹窗尚未完全露出时动画已结束，用户看不到效果
 *
 * 对齐参考：`OnboardingWelcomeModal` 的 `ONBOARDING_WELCOME_ANIM.drawerEnterDelayMs`
 */
const HEADLINE_ENTER_DELAY_MS = 300;

/**
 * Plan Welcome 弹窗内容（Figma H5 `45185:412591`，PC 壳 `45506:12455`）。
 *
 * 布局要点：
 * - 标题区用 `inset-x + flex justify-center` 水平居中，勿对标题用 `left-1/2 -translate-x-1/2`
 *   （会与 `.onboarding-plan-welcome-headline-enter` 的 `transform: translateY` 冲突，导致偏右被裁切）
 * - H5 标题 `top: 37.09%`（316/852），中间偏上；底部 CTA 贴底
 * - CTA 用 `emphasis_button`（accent4 `#d97757`），非 `primary_button`（brand 色）
 */
export const OnboardingPlanWelcomeModalContent: React.FC<
  OnboardingPlanWelcomeModalContentProps
> = ({ onClose }) => {
  const [isPc] = useIsPc();
  /** 为 true 时挂载 headline 入场 class；此前保持 opacity-0 占位 */
  const [playHeadlineEnter, setPlayHeadlineEnter] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let enterTimer = 0;
    let raf = 0;

    // 1) 等弹窗壳入场结束 → 2) 双 rAF 确保首帧已绘制 → 3) 再触发 CSS 动画
    enterTimer = window.setTimeout(() => {
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          if (!cancelled) {
            setPlayHeadlineEnter(true);
          }
        });
      });
    }, HEADLINE_ENTER_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(enterTimer);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className="relative h-full min-h-0 w-full overflow-hidden"
      data-testid="onboarding-plan-welcome-modal-view"
      data-figma-node-id="45185:412591"
    >
      {/* 全出血背景：webp + 渐变（pointer-events-none 不挡交互） */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        data-testid="onboarding-plan-welcome-background"
        data-figma-node-id="45223:73036"
      >
        <AppImage
          src={BACKGROUND_SRC}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          width={393}
          height={852}
          loading="eager"
          decoding="async"
        />
        <div
          className="absolute inset-0"
          style={{ backgroundImage: BACKGROUND_OVERLAY }}
        />
      </div>
      {/* 顶栏：H5 左上返回；PC 关闭按钮在内容层自绘（见 onboardingPlanWelcomeShell showClose: false） */}
      {!isPc ? (
        <div
          className="relative z-10 flex shrink-0 flex-col pt-safe pb-safe"
          data-testid="onboarding-plan-welcome-top-block"
          data-figma-node-id="45234:3510"
        >
          <div
            className="flex items-center px-4 py-2"
            data-testid="onboarding-plan-welcome-top-nav"
            data-figma-node-id="45234:3512"
          >
            <button
              type="button"
              onClick={() => onClose("close")}
              className="flex size-10 items-center justify-center rounded-[20px] bg-background_solid_neutral_primary"
              data-testid="onboarding-plan-welcome-back"
              data-figma-node-id="45234:3513"
              aria-label="Back"
            >
              <Icon
                name="left-arrow-alt"
                size={20}
                className="text-content_primary"
              />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onClose("close")}
          className="absolute right-6 top-6 z-20 flex size-6 items-center justify-center"
          data-testid="onboarding-plan-welcome-close"
          data-figma-node-id="45506:12458"
          aria-label="Close"
        >
          <Icon
            name="close"
            size={24}
            className="text-content_inverse_primary"
          />
        </button>
      )}
      {/* 标题：Figma y=316/852；外层负责定位与水平居中，内层 `<p>` 单独做 translateY 入场 */}
      <div
        className={cn(
          "absolute inset-x-4 z-10 flex justify-center",
          isPc ? "top-[32%]" : "top-[37.09%]",
        )}
        data-testid="onboarding-plan-welcome-headline-wrap"
      >
        <p
          className={cn(
            "w-full max-w-[345px] text-center headline_regular_m text-content_inverse_primary",
            playHeadlineEnter
              ? "onboarding-plan-welcome-headline-enter"
              : "translate-y-4 opacity-0",
          )}
          data-testid="onboarding-plan-welcome-headline"
          data-figma-node-id="45153:15312"
        >
          {I18n.t("You're all set — let's get your plan going.")}
        </p>
      </div>
      {/* 底部 CTA：Figma 贴底；H5 含 home indicator 安全区 */}
      <footer
        className={cn(
          "absolute bottom-0 left-0 right-0 z-10 px-4 pb-[28px]",
          isPc ? "px-6 pb-6" : "pb-8",
        )}
        data-testid="onboarding-plan-welcome-footer"
      >
        <button
          type="button"
          data-testid="onboarding-plan-welcome-cta"
          data-figma-node-id="45153:15714"
          className="button button_m emphasis_button flex h-11 w-full items-center justify-center rounded-2"
          {...gaTrackingAttrs("ob_paywall_view_plans")}
          data-openreplay-label={buildOpenReplayLabel(
            "Onboarding",
            "PlanWelcome",
            "ViewPlans",
            I18n.t("See my plan"),
          )}
          onClick={() => {
            onClose("view");
          }}
        >
          {I18n.t("See my plan")}
        </button>
      </footer>
    </div>
  );
};
